import React, { useState, useEffect } from 'react';
import { Modal, View, Text, TextInput, TouchableOpacity, StyleSheet, ScrollView } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import theme from '../config/theme';

type TypeOption = string | { value: string; label: string };
interface PromptDialogProps {
  visible: boolean;
  title: string;
  message?: string;
  placeholder?: string;
  initialValue?: string;
  types?: TypeOption[];
  initialType?: string;
  /** Rótulo do botão de confirmação. Default: "Adicionar" */
  confirmLabel?: string;
  onSubmit: (value: string, selectedType?: string) => void;
  onCancel: () => void;
}

const PromptDialog: React.FC<PromptDialogProps> = ({
  visible,
  title,
  message = '',
  placeholder = '',
  initialValue = '',
  types = [],
  initialType,
  confirmLabel = 'Adicionar',
  onSubmit,
  onCancel,
}) => {
  const [inputValue, setInputValue] = useState(initialValue);
  const getTypeValue = (t: TypeOption) => typeof t === 'string' ? t : t.value;
  const getTypeLabel = (t: TypeOption) => typeof t === 'string' ? t : t.label;
  const [selectedType, setSelectedType] = useState(initialType || (types.length ? getTypeValue(types[0]) : ''));
  const [showTypeList, setShowTypeList] = useState(false);

  // Quando o Modal passa de invisível -> visível, inicializamos os valores.
  // Mantemos estabilidade enquanto o usuário digita para não sobrescrever o que ele inserir.
  useEffect(() => {
    if (visible) {
      setInputValue(initialValue);
      setSelectedType(initialType || (types.length ? getTypeValue(types[0]) : ''));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const confirm = () => {
    onSubmit(inputValue.trim(), selectedType);
  };

  return (
    <Modal visible={visible} transparent animationType="fade">
      <View style={styles.overlay}>
        <View style={styles.container}>
          <Text style={styles.title}>{title}</Text>
          <TextInput
            style={styles.input}
            placeholder={placeholder}
            placeholderTextColor={theme.colors.text.tertiary}
            value={inputValue}
            onChangeText={setInputValue}
            autoFocus
          />
          {message !== '' && <Text style={styles.message}>{message}</Text>}
          {types.length > 0 && (
            <View style={styles.dropdownWrapper}>
              <TouchableOpacity style={styles.dropdownHeader} onPress={() => setShowTypeList(!showTypeList)}>
                <View style={styles.dropdownHeaderContent}>
                  <Text style={styles.dropdownHeaderText} numberOfLines={1}>
                    {getTypeLabel(types.find(t => getTypeValue(t) === selectedType) ?? types[0]) || 'Selecione o tipo'}
                  </Text>
                  <MaterialCommunityIcons
                    name="chevron-down"
                    size={20}
                    color={theme.colors.text.secondary}
                    style={{ transform: [{ rotate: showTypeList ? '180deg' : '0deg' }] }}
                  />
                </View>
              </TouchableOpacity>
              {showTypeList && (
                <ScrollView style={styles.dropdownList}>
                  {types.map(t => (
                    <TouchableOpacity key={getTypeValue(t)} style={styles.dropdownItem} onPress={() => { setSelectedType(getTypeValue(t)); setShowTypeList(false); }}>
                      <Text style={styles.dropdownItemText}>{getTypeLabel(t)}</Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              )}
            </View>
          )}
          <View style={styles.buttonRow}>
            <TouchableOpacity style={styles.button} onPress={onCancel}>
              <Text style={styles.buttonText}>Cancelar</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[styles.button, { backgroundColor: theme.colors.primary.default }]} onPress={confirm}>
              <Text style={[styles.buttonText, { color: theme.colors.text.inverse }]}>{confirmLabel}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    ...StyleSheet.absoluteFill,
    backgroundColor: 'rgba(0,0,0,0.3)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: {
    width: '85%',
    backgroundColor: theme.colors.background.primary,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.lg,
  },
  title: {
    fontSize: theme.fontSizes.lg,
    fontWeight: '600',
    color: theme.colors.text.primary,
    marginBottom: theme.spacing.sm,
  },
  message: {
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.secondary,
    marginBottom: theme.spacing.md,
  },
  input: {
    borderWidth: 1,
    borderColor: theme.colors.border.default,
    borderRadius: theme.borderRadius.sm,
    padding: theme.spacing.sm,
    fontSize: theme.fontSizes.md,
    marginBottom: theme.spacing.md,
    color: theme.colors.text.primary,
  },
  dropdownWrapper: {
    marginBottom: theme.spacing.md,
  },
  dropdownHeader: {
    borderWidth: 1,
    borderColor: theme.colors.border.default,
    borderRadius: theme.borderRadius.sm,
    padding: theme.spacing.sm,
  },
  dropdownHeaderContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  dropdownHeaderText: {
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.primary,
    flex: 1,
  },
  dropdownList: {
    maxHeight: 150,
    marginTop: 4,
    borderWidth: 1,
    borderColor: theme.colors.border.default,
    borderRadius: theme.borderRadius.sm,
  },
  dropdownItem: {
    padding: theme.spacing.sm,
  },
  dropdownItemText: {
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.primary,
  },
  buttonRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: theme.spacing.md,
  },
  button: {
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    borderRadius: theme.borderRadius.sm,
    marginLeft: theme.spacing.sm,
  },
  buttonText: {
    fontSize: theme.fontSizes.md,
    color: theme.colors.primary.default,
  },
});

export default PromptDialog;
